import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { getTeflHistoryInfo } from '../../../../api/tefl/get-tefl-history-info'
import useAuth from '../../../../lib/hooks/use-auth'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { TeflStayHistory } from '../../../../lib/types/models/tefl/tefl'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import DynamicDataTable from '../../../elements/dynamic-table'
import TeflAverageRating from './tefl-average-rating'

type Props = {
  olId: number
  teflId: number
}

const formatStayDates = (stay: TeflStayHistory) => {
  const { startDate, endDate } = stay.center
  if (!startDate || !endDate) return '-'
  return `${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`
}

const OlTeflSejourTable: React.FC<Props> = ({ olId, teflId }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isAdmin = user.role === RoleEnum.ADMIN

  const { data: historyInfo } = useQuery({
    queryKey: ['tefl-history-info', olId, teflId],
    queryFn: () => getTeflHistoryInfo(olId, teflId),
    enabled: isAdmin
  })

  const columns: Column<TeflStayHistory>[] = [
    ...(isAdmin
      ? [
          {
            enableSorting: true,
            header: t('tefl.history.columns.centers'),
            accessorKey: 'center.name',
            cell: ({ row }: { row: { original: TeflStayHistory } }) => (
              <Typography variant="secondary-body">{row.original.center.name}</Typography>
            )
          }
        ]
      : []),
    {
      enableSorting: true,
      header: t('tefl.history.columns.dates'),
      accessorKey: 'center.startDate',
      cell: ({ row }) => <Badge text={formatStayDates(row.original)} variant="grey" />
    },
    {
      header: t('tefl.history.columns.formula'),
      accessorKey: 'qualification',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 py-2">
          {row.original.center.centerToJourneys
            .map((ctj) => ctj.journey.date.product.formula)
            .map((formula, index) => (
              <Typography variant="secondary-body" key={index}>
                {getTranslatedName(formula)}
              </Typography>
            ))}
        </div>
      )
    },
    {
      // Computed by the backend per stay (provider side: diffused stays only), not sortable
      header: t('tefl.history.columns.average.rating'),
      accessorKey: 'averageRating',
      cell: ({ row }) => <TeflAverageRating {...row.original} />
    }
  ]

  return (
    <Box>
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        {t('tefl.history.title')}
      </Typography>
      <div className="h-4" />
      <Typography variant="label-semibold">{t('tefl.history.description')}</Typography>
      <div className="h-4" />
      <DynamicDataTable<TeflStayHistory>
        endpoint={constructApiUrl(API.tefl.getHistory.url, { olId, teflId })}
        headerType="none"
        isSearchable={false}
        columns={columns}
        footerCells={
          isAdmin
            ? [
                {
                  accessorKey: 'center.startDate',
                  value: (
                    <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                      {t('tefl.history.footer.average')}
                    </Typography>
                  )
                },
                {
                  accessorKey: 'averageRating',
                  value: <TeflAverageRating {...historyInfo} />
                }
              ]
            : undefined
        }
        permissions={{ delete: false }}
      />
    </Box>
  )
}

export default OlTeflSejourTable
